import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import { MetaConnectionGuard } from '@/shared/components/MetaConnectionGuard'
import { useMetaConnection } from '@/shared/contexts'
import { summaryApi } from './api'
import type { FullSummaryResponse, CountrySummary, DailySummary } from './types'
import { countryFlags } from './types'
import { OverallPerformanceSection, ProductsSection, ConfigurationRequired, type DailyData } from './components'
import type { HourlySummaryData, HourlyTotals } from './components/OverallPerformanceSection'

// Transform API daily data to chart format
function transformDailyData(apiDaily: DailySummary[]): DailyData[] {
  return apiDaily.map(d => ({
    date: d.date,
    spend: d.spend,
    revenue: d.revenue,
    blendedRoas: d.blended_roas,
    paidMediaRoas: d.paid_media_roas,
    grossProfit: d.gross_profit,
    cvr: d.cvr,
  }))
}

// ============================================================================
// Date Utilities
// ============================================================================

const CHART_PERIOD_DAYS = 15 // Charts always show last 15 days

function getDateString(date: Date): string {
  return date.toISOString().split('T')[0]
}

function getChartDateRange(referenceDate: Date): { start: string; end: string } {
  const end = new Date(referenceDate)
  const start = new Date(referenceDate)
  start.setDate(start.getDate() - (CHART_PERIOD_DAYS - 1)) // -14 days to get 15 total days
  return {
    start: getDateString(start),
    end: getDateString(end),
  }
}

// ============================================================================
// Date Picker Component (Single Day Selection)
// ============================================================================

type QuickDate = 'today' | 'yesterday' | null

// localStorage key for date picker visibility
const DATE_PICKER_STORAGE_KEY = 'trafiflow_date_picker_enabled'

function getDatePickerEnabled(): boolean {
  if (typeof window === 'undefined') return false
  return localStorage.getItem(DATE_PICKER_STORAGE_KEY) === 'true'
}

function setDatePickerEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(DATE_PICKER_STORAGE_KEY, enabled ? 'true' : 'false')
}

interface DatePickerProps {
  selectedQuickDate: QuickDate
  selectedDate: string
  onQuickDateSelect: (type: QuickDate) => void
  onDateSelect: (date: string, quickDate?: QuickDate) => void
  onSync?: () => void
  isSyncing?: boolean
}

function DatePicker({
  selectedQuickDate,
  selectedDate,
  onQuickDateSelect,
  onDateSelect,
  onSync,
  isSyncing,
}: DatePickerProps) {
  const [showDatePicker, setShowDatePicker] = useState(getDatePickerEnabled)
  const [customDate, setCustomDate] = useState(selectedDate)

  const handleQuickDate = (type: 'today' | 'yesterday') => {
    // Calculate the reference date (Today or Yesterday)
    const referenceDate = new Date()
    if (type === 'yesterday') {
      referenceDate.setDate(referenceDate.getDate() - 1)
    }
    
    const dateStr = getDateString(referenceDate)
    onQuickDateSelect(type)
    onDateSelect(dateStr, type)
    setCustomDate(dateStr)
  }

  const handleToggleDatePicker = () => {
    const newValue = !showDatePicker
    setShowDatePicker(newValue)
    setDatePickerEnabled(newValue)
  }

  const handleCustomDateChange = (date: string) => {
    setCustomDate(date)
    onQuickDateSelect(null)
    onDateSelect(date, null)
  }

  // Get formatted date for display
  const getDisplayDate = () => {
    if (selectedQuickDate === 'today') return 'Today'
    if (selectedQuickDate === 'yesterday') return 'Yesterday'
    if (selectedDate) {
      const date = new Date(selectedDate + 'T00:00:00')
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      })
    }
    return 'Select date'
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Quick Date Selectors - Yesterday / Today */}
      <div className="flex border border-tf-border rounded-lg overflow-hidden">
        <button
          onClick={() => handleQuickDate('yesterday')}
          className={cn(
            'px-4 py-1.5 text-sm font-medium transition-colors',
            selectedQuickDate === 'yesterday'
              ? 'bg-tf-text text-white'
              : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
          )}
        >
          Yesterday
        </button>
        <div className="w-px bg-tf-border" />
        <button
          onClick={() => handleQuickDate('today')}
          className={cn(
            'px-4 py-1.5 text-sm font-medium transition-colors',
            selectedQuickDate === 'today'
              ? 'bg-tf-text text-white'
              : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
          )}
        >
          Today
        </button>
      </div>

      {/* Toggle for calendar picker */}
      <button
        onClick={handleToggleDatePicker}
        className={cn(
          'p-1.5 rounded-lg transition-colors',
          showDatePicker
            ? 'bg-tf-feedback-info-bg text-tf-feedback-info-text'
            : 'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-muted'
        )}
        title={showDatePicker ? 'Hide date picker' : 'Pick a specific date'}
      >
        <Icons.Calendar className="w-4 h-4" />
      </button>

      {/* Single Date Picker (hidden by default, shown via toggle) */}
      {showDatePicker && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={customDate}
            max={getDateString(new Date())}
            onChange={(e) => handleCustomDateChange(e.target.value)}
            className={cn(
              'px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary',
              selectedQuickDate === null 
                ? 'border-primary bg-tf-feedback-info-bg' 
                : 'border-tf-border'
            )}
          />
        </div>
      )}

      {/* Current selection indicator when calendar is hidden */}
      {!showDatePicker && selectedQuickDate === null && selectedDate && (
        <span className="text-sm text-tf-text-muted bg-tf-bg-muted px-3 py-1.5 rounded-lg">
          {getDisplayDate()}
        </span>
      )}

      {onSync && (
        <button
          onClick={onSync}
          disabled={isSyncing}
          title="Syncs only missing data (yesterday, today, and gaps) - Fast"
          className="flex items-center gap-2 px-4 py-1.5 border border-tf-border text-tf-text text-sm font-medium rounded-lg hover:bg-tf-bg-muted transition-colors disabled:opacity-50"
        >
          {isSyncing ? (
            <Icons.RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Icons.Zap className="w-4 h-4" />
          )}
          Smart Sync
        </button>
      )}
    </div>
  )
}


// ============================================================================
// Country Card Component
// ============================================================================

function CountryCard({
  country,
  isSelected,
  onClick,
}: {
  country: CountrySummary
  isSelected: boolean
  onClick: () => void
}) {
  const flag = countryFlags[country.country_code] || '🏳️'

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-shrink-0 p-4 rounded-xl border transition-all min-w-[180px]',
        isSelected
          ? 'bg-tf-feedback-info-bg border-primary shadow-sm'
          : 'bg-tf-bg-card border-tf-border hover:border-tf-border-sub hover:shadow-sm'
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-2xl">{flag}</span>
        <span className="font-medium text-tf-text">{country.country}</span>
      </div>
      <div className="space-y-1">
        <p className="text-xs text-tf-text-muted">Blended ROAS</p>
        <p className={cn('text-xl font-bold', country.roas >= 1 ? 'text-tf-text' : 'text-tf-feedback-error-text')}>
          {country.roas.toFixed(2)}
        </p>
      </div>
      <div className="mt-2 pt-2 border-t border-tf-border-sub">
        <p className="text-xs text-tf-text-muted">Spend</p>
        <p className="text-lg font-semibold text-tf-text">
          ${country.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </p>
      </div>
    </button>
  )
}

// ============================================================================
// Countries Section Component
// ============================================================================

// Check if countries are properly parsed from campaign names
function areCountriesParsedCorrectly(countries: CountrySummary[], totalCampaigns: number): boolean {
  if (countries.length === 0) return false // No countries parsed is a problem
  
  // Count campaigns that have known country codes
  const knownCountryCodes = ['US', 'CA', 'AU', 'UK', 'NZ', 'SG', 'DK', 'FI', 'NL', 'SE', 'DE', 'WW']
  const campaignsWithKnownCountries = countries
    .filter(c => knownCountryCodes.includes(c.country_code))
    .reduce((sum, c) => sum + c.campaigns, 0)

  // If less than 50% of campaigns have recognized countries, it might be a parsing issue
  if (totalCampaigns > 0) {
    const ratio = campaignsWithKnownCountries / totalCampaigns
    return ratio >= 0.5
  }
  
  return true
}

function CountriesSection({
  countries,
  selectedCountry,
  onSelectCountry,
  loading,
  totalCampaigns = 0,
}: {
  countries: CountrySummary[]
  selectedCountry: string | null
  onSelectCountry: (code: string | null) => void
  loading: boolean
  totalCampaigns?: number
}) {
  // Calculate totals for "All Countries"
  const totals = countries.reduce(
    (acc, c) => ({
      spend: acc.spend + c.spend,
      revenue: acc.revenue + c.revenue,
      campaigns: acc.campaigns + c.campaigns,
      active: acc.active + c.active,
    }),
    { spend: 0, revenue: 0, campaigns: 0, active: 0 }
  )
  const blendedRoas = totals.spend > 0 ? totals.revenue / totals.spend : 0

  // Check if countries are properly parsed
  const countriesAreParsed = areCountriesParsedCorrectly(countries, totalCampaigns)

  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icons.Globe className="w-5 h-5 text-tf-text-muted" />
          <h3 className="font-semibold text-tf-text">Countries</h3>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex-shrink-0 w-[180px] h-[140px] bg-tf-bg-hover rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  // Show configuration message if countries couldn't be parsed
  if (!countriesAreParsed && totalCampaigns > 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Icons.Globe className="w-5 h-5 text-tf-text-muted" />
          <h3 className="font-semibold text-tf-text">Countries</h3>
        </div>
        <ConfigurationRequired
          title="Country Data Not Available"
          message="We couldn't extract country information from your campaign names. To see country-level analytics, your campaigns need to include country codes in their names."
          details={[
            'Country code should be in the format: _US_, _CA_, _AU_, _UK_, etc.',
            'Example: 10035_SWEETHEART_PPT_US_ACQ_VOLUME_CBO',
            'The country code is typically in position 4 of the naming convention',
          ]}
        />
      </div>
    )
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icons.Globe className="w-5 h-5 text-tf-text-muted" />
          <h3 className="font-semibold text-tf-text">Countries</h3>
        </div>
        {selectedCountry && (
          <button
            onClick={() => onSelectCountry(null)}
            className="text-sm text-tf-feedback-info-text hover:text-tf-feedback-info-text"
          >
            Clear filter
          </button>
        )}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2">
        {/* All Countries Card */}
        <button
          onClick={() => onSelectCountry(null)}
          className={cn(
            'flex-shrink-0 p-4 rounded-xl border transition-all min-w-[180px]',
            selectedCountry === null
              ? 'bg-tf-feedback-info-bg border-primary shadow-sm'
              : 'bg-tf-bg-card border-tf-border hover:border-tf-border-sub hover:shadow-sm'
          )}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">🌍</span>
            <span className="font-medium text-tf-text">All Countries</span>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-tf-text-muted">Blended ROAS</p>
            <p className={cn('text-xl font-bold', blendedRoas >= 1 ? 'text-tf-text' : 'text-tf-feedback-error-text')}>
              {blendedRoas.toFixed(2)}
            </p>
          </div>
          <div className="mt-2 pt-2 border-t border-tf-border-sub">
            <p className="text-xs text-tf-text-muted">Spend</p>
            <p className="text-lg font-semibold text-tf-text">
              ${totals.spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </div>
        </button>

        {/* Individual Country Cards */}
        {countries.map((country) => (
          <CountryCard
            key={country.country_code}
            country={country}
            isSelected={selectedCountry === country.country_code}
            onClick={() => onSelectCountry(selectedCountry === country.country_code ? null : country.country_code)}
          />
        ))}
      </div>
    </div>
  )
}


// ============================================================================
// Main Summary Content Component
// ============================================================================

function SummaryContent() {
  const { connection, smartSync } = useMetaConnection()
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)
  const [summaryData, setSummaryData] = useState<FullSummaryResponse | null>(null) // Single day data for metric cards
  const [chartData, setChartData] = useState<FullSummaryResponse | null>(null) // 15-day data for charts
  const [hourlyData, setHourlyData] = useState<HourlySummaryData[] | null>(null)
  const [hourlyTotals, setHourlyTotals] = useState<HourlyTotals | null>(null)
  const [currentHour, setCurrentHour] = useState(new Date().getHours())
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [selectedQuickDate, setSelectedQuickDate] = useState<QuickDate>('today')
  
  // Initialize with today's date
  const [selectedDate, setSelectedDate] = useState(() => getDateString(new Date()))

  // Fetch hourly data separately
  const fetchHourlyData = useCallback(async () => {
    try {
      const result = await summaryApi.getHourly()
      if (result.success && result.data) {
        // Transform API response to component format
        const transformedHourly: HourlySummaryData[] = result.data.hourly.map(h => ({
          hour: h.hour,
          yesterday_total: h.yesterday_total,
          yesterday: h.yesterday,
          today: h.today,
        }))
        setHourlyData(transformedHourly)
        setHourlyTotals(result.data.totals)
        setCurrentHour(result.data.meta.current_hour)
      }
    } catch (err) {
      console.error('Error fetching hourly data:', err)
    }
  }, [])

  const fetchData = useCallback(async (date?: string, country?: string | null) => {
    setIsLoading(true)
    const effectiveDate = date || selectedDate
    const effectiveCountry = country === undefined ? selectedCountry : country

    // Calculate 15-day range ending on selected date
    const chartRange = getChartDateRange(new Date(effectiveDate + 'T00:00:00'))

    try {
      // Fetch single-day data for metric cards AND 15-day data for charts in parallel
      const [singleDayResult, chartDataResult] = await Promise.all([
        summaryApi.getFullSummary(effectiveDate, effectiveDate, effectiveCountry || undefined),
        summaryApi.getFullSummary(chartRange.start, chartRange.end, effectiveCountry || undefined),
        fetchHourlyData(),
      ])
      
      if (singleDayResult.success && singleDayResult.data) {
        setSummaryData(singleDayResult.data)
      }
      if (chartDataResult.success && chartDataResult.data) {
        setChartData(chartDataResult.data)
      }
    } catch (err) {
      console.error('Error fetching summary:', err)
    } finally {
      setIsLoading(false)
    }
  }, [selectedDate, selectedCountry, fetchHourlyData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleDateSelect = (date: string, quickDate?: QuickDate) => {
    setSelectedDate(date)
    if (quickDate !== undefined) {
      setSelectedQuickDate(quickDate)
    }
    fetchData(date, undefined)
  }

  const handleCountrySelect = (code: string | null) => {
    setSelectedCountry(code)
    fetchData(selectedDate, code)
  }

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      await smartSync()

      // After sync, if today is selected but has no spend data, switch to yesterday
      // (Meta often has no intra-day data until late in the day)
      if (selectedQuickDate === 'today') {
        const todayResult = await summaryApi.getFullSummary(selectedDate, selectedDate, selectedCountry || undefined)
        if (todayResult.success && (todayResult.data?.overall?.spend ?? 0) === 0) {
          const yesterday = getDateString(new Date(new Date().setDate(new Date().getDate() - 1)))
          setSelectedDate(yesterday)
          setSelectedQuickDate('yesterday')
          await fetchData(yesterday, undefined)
          return
        }
      }

      await fetchData()
    } finally {
      setIsSyncing(false)
    }
  }

  // Use summaryData for both metric cards and any displays
  const cardMetrics = summaryData?.overall

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-tf-text">Summary</h1>
          <p className="text-sm text-tf-text-muted">Marketing performance overview</p>
        </div>
        <DatePicker
          selectedQuickDate={selectedQuickDate}
          selectedDate={selectedDate}
          onQuickDateSelect={(type) => setSelectedQuickDate(type)}
          onDateSelect={handleDateSelect}
          onSync={handleSync}
          isSyncing={isSyncing}
        />
      </div>

      {/* Connection Status */}
      {connection?.connected && (
        <div className="flex items-center gap-2 px-4 py-3 bg-tf-feedback-success-bg border border-tf-feedback-success-border rounded-lg">
          <div className="w-2 h-2 bg-tf-success rounded-full" />
          <span className="text-sm text-tf-feedback-success-text font-medium">
            Connected to {connection.accountName}
            {selectedQuickDate && (
              <span className="ml-2 text-tf-feedback-success-text">
                - {selectedQuickDate === 'today' ? 'Today' : 'Yesterday'}
              </span>
            )}
          </span>
          {connection.lastSyncAt && (
            <span className="text-xs text-tf-feedback-success-text ml-auto">
              Last synced: {new Date(connection.lastSyncAt).toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}
            </span>
          )}
        </div>
      )}

      {/* Countries - Top Section */}
      <CountriesSection
        countries={summaryData?.countries || []}
        selectedCountry={selectedCountry}
        onSelectCountry={handleCountrySelect}
        totalCampaigns={summaryData?.overall?.total_campaigns || 0}
        loading={isLoading}
      />

      {/* Overall Performance Section with Charts */}
      <OverallPerformanceSection
        metrics={cardMetrics ? {
          blendedRoas: cardMetrics.blended_roas,
          targetBlendedRoas: cardMetrics.blended_roas * 0.9, // Mock previous period
          spend: cardMetrics.spend,
          targetSpend: cardMetrics.spend * 0.85,
          grossRevenue: cardMetrics.gross_revenue,
          targetGrossRevenue: cardMetrics.gross_revenue * 0.92,
          grossProfit: cardMetrics.gross_profit,
          targetGrossProfit: cardMetrics.gross_profit * 0.88,
          cvr: cardMetrics.cvr,
          targetCvr: cardMetrics.cvr * 0.95,
        } : null}
        dailyData={transformDailyData(chartData?.daily || [])}
        hourlyData={hourlyData || undefined}
        hourlyTotals={hourlyTotals || undefined}
        currentHour={currentHour}
        loading={isLoading}
        onRefresh={handleSync}
        cogsPercentage={summaryData?.meta?.cogs_percentage || 0}
      />

      {/* Products Section with Chart/List Toggle */}
      {/* Charts use chartData (15-day trends with daily_data), List uses summaryData (single day) */}
      <ProductsSection 
        products={chartData?.products || []} 
        listProducts={summaryData?.products || []}
        loading={isLoading}
      />
    </div>
  )
}

// ============================================================================
// Main Summary Page Component
// ============================================================================

export function SummaryPage() {
  return (
    <MetaConnectionGuard>
      <SummaryContent />
    </MetaConnectionGuard>
  )
}

export default SummaryPage
